import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  People,
  School,
  Public,
  Celebration,
  ArrowForward,
  Star,
  Event,
  Restaurant,
  Movie,
  Campaign,
  Language,
  GroupWork
} from '@mui/icons-material';

// --- Styled Components ---

const PageWrapper = styled.div`
  background-color: var(--color-bg);
  overflow-x: hidden;
`;

const Section = styled.section`
  padding: 120px 24px;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 80px 20px;
  }
`;

const HeroContainer = styled.div`
  min-height: 90vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start; // Left align for editorial look
  padding: 120px 24px;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
`;

const BrandOverline = styled(motion.span)`
  font-family: var(--font-serif);
  color: var(--color-primary-blue);
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin-bottom: 16px;
  display: block;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(3.5rem, 8vw, 7rem); // Massive responsive font
  font-weight: 400; // Lighter weight for elegance
  line-height: 1;
  color: var(--color-text);
  margin-bottom: 2rem;
  letter-spacing: -0.03em;
  
  span {
    color: var(--color-primary-blue);
    font-style: italic;
    font-family: var(--font-serif);
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1.2rem, 2vw, 1.8rem);
  color: var(--color-text-light);
  max-width: 600px;
  line-height: 1.5;
  margin-bottom: 3rem;
  font-weight: 300;
`;

const Button = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background-color: var(--color-text);
  color: white;
  padding: 16px 32px;
  border-radius: 4px; // Sharp corners or slightly rounded
  font-family: var(--font-sans);
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.1rem;

  &:hover {
    background-color: var(--color-primary-blue);
    transform: translateY(-2px);
  }
`;

// --- Manifesto Section ---

const ManifestoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ManifestoTitle = styled.h2`
  font-size: clamp(2.5rem, 5vw, 4rem);
  margin-bottom: 24px;
  font-family: var(--font-serif);
`;

const ManifestoValid = styled.div`
  font-size: 1.25rem;
  line-height: 1.7;
  color: var(--color-text-light);
  
  p {
    margin-bottom: 24px;
  }
`;

// --- Stats Strip ---

const StatsStrip = styled.div`
  border-top: 1px solid rgba(0,0,0,0.1);
  border-bottom: 1px solid rgba(0,0,0,0.1);
  padding: 60px 0;
  margin: 60px 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatItem = styled.div`
  text-align: center;
  
  .number {
    font-family: var(--font-serif);
    font-size: 3.5rem;
    font-weight: 700;
    color: var(--color-primary-blue);
    display: block;
    margin-bottom: 8px;
  }
  
  .label {
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--color-text-light);
  }
`;

// --- Offerings Grid (Bento Box) ---

const GridTitle = styled.h2`
  font-size: 3rem;
  margin-bottom: 48px;
  text-align: left;
`;

const BentoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  
  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const BentoCard = styled(motion.div)`
  background: white;
  padding: 32px;
  border: 1px solid rgba(0,0,0,0.05);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.02);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
    border-color: var(--color-primary-blue);
    
    .icon-box {
      background-color: var(--color-primary-blue);
      color: white;
    }
  }
`;

const IconBox = styled.div`
  width: 50px;
  height: 50px;
  background-color: rgba(2, 74, 159, 0.1);
  color: var(--color-primary-blue);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  transition: all 0.3s ease;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 16px;
  font-family: var(--font-sans);
  font-weight: 600;
`;

const CardText = styled.p`
  color: var(--color-text-light);
  font-size: 1rem;
  line-height: 1.6;
`;

// --- Projects (List View) ---

const ProjectList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0; // Seamless border look
`;

const ProjectItem = styled(motion.div)`
  display: grid;
  grid-template-columns: 100px 1.5fr 2fr 1fr;
  align-items: start;
  padding: 40px 0;
  border-top: 1px solid rgba(0,0,0,0.1);
  gap: 24px;
  
  &:last-child {
    border-bottom: 1px solid rgba(0,0,0,0.1);
  }
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Tag = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
  background-color: ${props => props.color || '#eee'};
  color: white;
`;

const FeatureList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  
  span {
    font-size: 0.85rem;
    color: var(--color-text-light);
    background: #f5f5f5;
    padding: 2px 8px;
    border-radius: 4px;
  }
`;

// --- Data ---
const stats = [
  { icon: People, number: "50+", label: "Members" },
  { icon: Celebration, number: "10+", label: "Events" },
  { icon: School, number: "1", label: "University" },
  { icon: Public, number: "∞", label: "Network" }
];

const offerings = [
  {
    icon: Event,
    title: "Cultural Events",
    description: "Authentic celebrations of Israeli holidays including Yom Hatsma'ut, Rosh Hashana, and Hanukkah.",
  },
  {
    icon: Campaign,
    title: "Academic Seminars",
    description: "Expert-led seminars covering history, technology, and contemporary issues.",
  },
  {
    icon: GroupWork,
    title: "Community",
    description: "A home away from home. Forge meaningful bonds with fellow students.",
  },
  {
    icon: Movie,
    title: "Screenings",
    description: "Award-winning movies and documentaries reflecting Israeli society.",
  },
  {
    icon: Restaurant,
    title: "Culinary",
    description: "Workshops and tastings. From Hummus to Shakshuka.",
  },
  {
    icon: Language,
    title: "Language",
    description: "Hebrew learning sessions and conversation groups.",
  }
];

const futureProjects = [
  {
    title: "Israel Tech Tour",
    description: "7-day educational journey combining cultural immersion with tech exploration.",
    timeline: "Feb 2026",
    features: ["Tech Visits", "Historical Sites", "Networking"],
    color: "#FF6B9D"
  },
  {
    title: "SHALOM Hub",
    description: "A permanent community center within EPFL campus for collaboration.",
    timeline: "Fall 2025",
    features: ["Study Areas", "Event Space", "Library"],
    color: "#4F8EF7"
  },
  {
    title: "Tech Exchange",
    description: "Semester exchange programs connecting EPFL with Israeli universities.",
    timeline: "2026",
    features: ["Exchange", "Research", "Credits"],
    color: "#50E3C2"
  },
  {
    title: "Startup Incubator",
    description: "Mentorship and funding opportunities for student entrepreneurs.",
    timeline: "2025-26",
    features: ["Mentorship", "Funding", "Workshops"],
    color: "#FFB74D"
  },
];

const Home = () => {
  return (
    <PageWrapper>
      {/* Hero */}
      <HeroContainer>
        <BrandOverline
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          SHALOM
        </BrandOverline>
        <HeroTitle
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          Culture Meets <br /> <span>Innovation</span> at EPFL.
        </HeroTitle>
        <HeroSubtitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Connecting students through Israeli heritage, academic excellence,
          and the global innovation ecosystem.
        </HeroSubtitle>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Button to="/become-a-member">
            Become a Member <ArrowForward fontSize="small" />
          </Button>
        </motion.div>
      </HeroContainer>

      {/* Manifesto / About */}
      <Section style={{ backgroundColor: '#F3F4F6', borderRadius: '20px' }}>
        <ManifestoGrid>
          <div>
            <ManifestoTitle>Our Mission</ManifestoTitle>
          </div>
          <ManifestoValid>
            <p>
              SHALOM was established in April 2024 with a clear vision: to create a bridge between Israeli culture and academic excellence at EPFL.
            </p>
            <p>
              We believe in fostering an inclusive environment where students can explore Israeli heritage while building meaningful connections. Our core values center around cultural appreciation, academic excellence, and innovation.
            </p>
          </ManifestoValid>
        </ManifestoGrid>

        <StatsStrip>
          {stats.map((s, i) => (
            <StatItem key={i}>
              <span className="number">{s.number}</span>
              <span className="label">{s.label}</span>
            </StatItem>
          ))}
        </StatsStrip>
      </Section>

      {/* Offerings */}
      <Section>
        <GridTitle>What We Do</GridTitle>
        <BentoGrid>
          {offerings.map((offer, i) => (
            <BentoCard
              key={i}
              whileHover={{ y: -5 }}
            >
              <IconBox className="icon-box">
                <offer.icon />
              </IconBox>
              <CardTitle>{offer.title}</CardTitle>
              <CardText>{offer.description}</CardText>
            </BentoCard>
          ))}
        </BentoGrid>
      </Section>

      {/* Projects */}
      <Section>
        <GridTitle>Future Projects</GridTitle>
        <ProjectList>
          {futureProjects.map((p, i) => (
            <ProjectItem
              key={i}
              initial={{ opacity: 0.8 }}
              whileHover={{ opacity: 1, backgroundColor: 'rgba(0,0,0,0.01)' }}
            >
              <div style={{ fontWeight: 'bold', color: '#ccc', fontSize: '1.2rem' }}>0{i + 1}</div>
              <div>
                <h3 style={{ fontSize: '1.5rem', fontFamily: 'var(--font-serif)', margin: 0 }}>{p.title}</h3>
                <div style={{ marginTop: '8px' }}>
                  <Tag color={p.color}>{p.timeline}</Tag>
                </div>
              </div>
              <p style={{ margin: 0, color: 'var(--color-text-light)' }}>
                {p.description}
              </p>
              <FeatureList>
                {p.features.map(f => <span key={f}>{f}</span>)}
              </FeatureList>
            </ProjectItem>
          ))}
        </ProjectList>
      </Section>

      {/* CTA Box */}
      <div style={{ padding: '0 24px 120px' }}>
        <motion.div style={{
          background: 'var(--color-primary-blue)',
          borderRadius: '20px',
          padding: '80px 40px',
          textAlign: 'center',
          color: 'white',
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <Star style={{ fontSize: 60, color: 'var(--color-highlight)', marginBottom: 24 }} />
            <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontFamily: 'var(--font-serif)', marginBottom: 24 }}>
              Join the Community
            </h2>
            <p style={{ fontSize: '1.2rem', maxWidth: '600px', margin: '0 auto 48px', opacity: 0.9 }}>
              Be part of a growing network of students and innovators.
            </p>
            <Button to="/become-a-member" style={{ background: 'white', color: 'var(--color-primary-blue)' }}>
              Become a Member
            </Button>
          </div>

          {/* Abstract Background Decoration */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            left: '-10%',
            width: '600px',
            height: '600px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '50%',
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-50%',
            right: '-10%',
            width: '500px',
            height: '500px',
            background: 'rgba(255,255,255,0.08)',
            borderRadius: '50%',
          }} />
        </motion.div>
      </div>

    </PageWrapper>
  );
};

export default Home;
